(() => {
  'use strict';

  if (window.__nonstaffSearchDrawerInitialized) return;
  window.__nonstaffSearchDrawerInitialized = true;

  const SELECTORS = {
    master: '#NonstaffTagMaster',
    drawer: '[data-nonstaff-search-drawer]',
    panel: '.nonstaff-search-drawer__panel',
    form: '[data-nonstaff-search-form]',
    open: '[data-nonstaff-search-open]',
    close: '[data-nonstaff-search-close]',
    genderTabs: '[data-nonstaff-gender-tabs]',
    filterGroups: '[data-nonstaff-filter-groups]',
    selectedArea: '[data-nonstaff-selected-area]',
    selectedList: '[data-nonstaff-selected-list]',
    clear: '[data-nonstaff-clear]',
    keyword: '[data-nonstaff-keyword]',
    keywordClear: '[data-nonstaff-keyword-clear]',
    message: '[data-nonstaff-search-message]',
    groupTemplate: '#NonstaffFilterGroupTemplate',
    optionTemplate: '#NonstaffFilterOptionTemplate',
    selectedTemplate: '#NonstaffSelectedTagTemplate'
  };

  /*
    同じ意味なのに、既存商品側でタグ表記が分かれているもの。
    ここに追加すれば、今後ほかの表記ゆれも同じ方式で吸収できる。

    例:
    "スウェット" を選択 / 入力
    ↓
    以下のどれかを持つ商品を対象にする。
  */
  const DEFAULT_SEARCH_ALIASES = {
    item: {
      スウェット: [
        'スウェット',
        'スウェット トレーナー',
        'トレーナー・スウェット'
      ]
    }
  };

  const state = {
    master: null,
    drawer: null,
    panel: null,
    form: null,
    keywordInput: null,
    lastFocusedElement: null,
    closeTimer: null,

    selected: new Map(),
    sizeAny: new Map(),
    availableValues: new Map(),
    contentReady: false,

    brandCounts: new Map(),
    popularBrands: [],
    brandRankingReady: false,
    brandRankingPromise: null
  };

  const unique = (values) => [
    ...new Set(
      (values || []).filter(Boolean)
    )
  ];

  const normalizeSearchText = (value) => {
    return String(value || '')
      .normalize('NFKC')
      .toLocaleLowerCase('ja-JP')
      .trim();
  };

  const escapeSearchValue = (value) => {
    return String(value || '')
      .replace(/\\/g, '\\\\')
      .replace(/"/g, '\\"');
  };

  const parseTagMaster = () => {
    const element = document.querySelector(
      SELECTORS.master
    );

    if (!element) {
      console.error(
        'Non-Staff: タグマスターが見つかりません。'
      );
      return null;
    }

    try {
      return JSON.parse(
        element.textContent
      );
    } catch (error) {
      console.error(
        'Non-Staff: タグマスターのJSONが不正です。',
        error
      );
      return null;
    }
  };

  const getDisplayLabel = (
    groupKey,
    tag
  ) => {
    const customLabel =
      state.master
        ?.displayLabels?.[tag];

    if (customLabel) {
      return customLabel;
    }

    if (groupKey === 'labelSize') {
      return String(tag || '')
        .replace(
          /^表記サイズ/,
          ''
        );
    }

    if (groupKey === 'fitSize') {
      return String(tag || '')
        .replace(
          /^着用感/,
          ''
        )
        .replace(
          /^実寸/,
          ''
        );
    }

    return tag;
  };

  const getGenderTabLabel = (value) => {
    const labels = {
      メンズ: 'MEN',
      レディース: 'WOMEN',
      ユニセックス: 'UNISEX'
    };

    return labels[value] || value;
  };

  const getSearchTagsForSelection = (
    groupKey,
    tag
  ) => {
    const configuredAliases =
      state.master
        ?.searchAliases
        ?.[groupKey]
        ?.[tag];

    const defaultAliases =
      DEFAULT_SEARCH_ALIASES
        ?.[groupKey]
        ?.[tag];

    return unique([
      tag,
      ...(
        Array.isArray(configuredAliases)
          ? configuredAliases
          : []
      ),
      ...(
        Array.isArray(defaultAliases)
          ? defaultAliases
          : []
      )
    ]);
  };

  const buildTagClause = (tags) => {
    const clauses = unique(tags)
      .map((tag) => {
        return `tag:"${escapeSearchValue(tag)}"`;
      });

    if (clauses.length === 0) {
      return '';
    }

    if (clauses.length === 1) {
      return clauses[0];
    }

    return `(${clauses.join(' OR ')})`;
  };

  /* =========================================================
     Category → Item Parent / Child
  ========================================================= */

  const getAllowedItemsForCategory = (
    category
  ) => {
    const categoryItems =
      state.master
        ?.categoryItems || {};

    if (!category) {
      return [];
    }

    const candidateKeys =
      category ===
        'ジャケット・アウター'
        ? [
            'ジャケット・アウター',
            'アウター'
          ]
        : [category];

    for (const key of candidateKeys) {
      const items =
        categoryItems[key];

      if (Array.isArray(items)) {
        return items;
      }
    }

    return [];
  };

  const normalizeItemSelection = () => {
    const category =
      state.selected.get(
        'category'
      ) || '';

    const selectedItem =
      state.selected.get(
        'item'
      ) || '';

    if (!selectedItem) {
      return;
    }

    const allowedItems =
      new Set(
        getAllowedItemsForCategory(
          category
        )
      );

    if (
      !category ||
      !allowedItems.has(
        selectedItem
      )
    ) {
      state.selected.delete(
        'item'
      );
    }
  };

  const updateItemGroupVisibility = () => {
    document
      .querySelectorAll(
        '[data-nonstaff-category-children]'
      )
      .forEach((element) => {
        element.remove();
      });

    const category =
      state.selected.get(
        'category'
      ) || '';

    if (!category) {
      return;
    }

    const categoryDetails =
      document.querySelector(
        '[data-nonstaff-filter-group][data-group-key="category"]'
      );

    if (!categoryDetails) {
      return;
    }

    const selectedCategoryInput = [
      ...categoryDetails
        .querySelectorAll(
          '[data-nonstaff-option-input]'
        )
    ].find((input) => {
      return (
        input.dataset
          .tagValue ===
        category
      );
    });

    if (!selectedCategoryInput) {
      return;
    }

    const selectedCategoryOption =
      selectedCategoryInput
        .closest(
          '[data-nonstaff-filter-option]'
        );

    if (!selectedCategoryOption) {
      return;
    }

    const allowedItems =
      getAllowedItemsForCategory(
        category
      );

    const availableItems =
      new Set(
        state.availableValues
          .get('item') || []
      );

    const childItems =
      allowedItems.filter(
        (tag) => {
          return availableItems.has(
            tag
          );
        }
      );

    if (childItems.length === 0) {
      return;
    }

    const childContainer =
      document.createElement(
        'div'
      );

    childContainer.className =
      'nonstaff-category-children';

    childContainer.dataset
      .nonstaffCategoryChildren =
      '';

    childItems.forEach((tag) => {
      const option =
        createOption(
          'item',
          tag
        );

      if (option) {
        childContainer
          .appendChild(
            option
          );
      }
    });

    selectedCategoryOption
      .insertAdjacentElement(
        'afterend',
        childContainer
      );
  };

  /* =========================================================
     Size
  ========================================================= */

  const getSizeMode = () => {
    const category =
      state.selected.get(
        'category'
      ) || '';

    if (
      [
        'トップス',
        'ジャケット・アウター',
        'アウター'
      ].includes(category)
    ) {
      return 'clothing';
    }

    if (category === 'パンツ') {
      return 'pants';
    }

    if (!category) {
      return 'mixed';
    }

    return 'none';
  };

  const getSizeTagInformation = (
    groupKey,
    tag
  ) => {
    if (groupKey === 'labelSize') {
      const clothingMatch =
        String(tag || '')
          .match(
            /^表記サイズ(メンズ|レディース)(.+)$/
          );

      if (clothingMatch) {
        return {
          mode: 'clothing',
          gender:
            clothingMatch[1],
          size:
            clothingMatch[2]
        };
      }

      const pantsMatch =
        String(tag || '')
          .match(
            /^表記サイズ(W\d+)$/
          );

      if (pantsMatch) {
        return {
          mode: 'pants',
          gender: '',
          size:
            pantsMatch[1]
        };
      }

      if (
        [
          '表記サイズ表記なし',
          '表記サイズ不明'
        ].includes(tag)
      ) {
        return {
          mode: 'universal',
          gender: '',
          size: tag
        };
      }
    }

    if (groupKey === 'fitSize') {
      const clothingMatch =
        String(tag || '')
          .match(
            /^着用感(メンズ|レディース)(.+)$/
          );

      if (clothingMatch) {
        return {
          mode: 'clothing',
          gender:
            clothingMatch[1],
          size:
            clothingMatch[2]
        };
      }

      const pantsMatch =
        String(tag || '')
          .match(
            /^実寸(W\d+)相当$/
          );

      if (pantsMatch) {
        return {
          mode: 'pants',
          gender: '',
          size:
            pantsMatch[1]
        };
      }
    }

    return {
      mode: 'other',
      gender: '',
      size: ''
    };
  };

  const getFitSizeTag = (
    gender,
    size
  ) => {
    if (
      ![
        'メンズ',
        'レディース'
      ].includes(gender) ||
      !size
    ) {
      return '';
    }

    return `着用感${gender}${size}`;
  };

  const fitSizeExistsForAnyGender = (
    size
  ) => {
    if (!size) {
      return false;
    }

    const availableValues =
      new Set(
        state.availableValues
          .get('fitSize') || []
      );

    const storeTags =
      new Set(
        state.master
          ?.allStoreTags || []
      );

    return [
      'メンズ',
      'レディース'
    ].some((gender) => {
      const tag =
        getFitSizeTag(
          gender,
          size
        );

      if (
        !availableValues.has(tag)
      ) {
        return false;
      }

      return (
        storeTags.size === 0 ||
        storeTags.has(tag)
      );
    });
  };

  const getSizeGroupLabel = (
    groupKey
  ) => {
    const mode =
      getSizeMode();

    if (groupKey === 'labelSize') {
      if (mode === 'pants') {
        return '表記ウエスト';
      }

      if (mode === 'mixed') {
        return (
          '表記サイズ / 表記ウエスト'
        );
      }

      return '表記サイズ';
    }

    if (groupKey === 'fitSize') {
      if (mode === 'pants') {
        return '実寸ウエスト';
      }

      if (mode === 'mixed') {
        return (
          '着用感サイズ / 実寸ウエスト'
        );
      }

      return '着用感サイズ';
    }

    return (
      state.master
        ?.groups?.[groupKey]
        ?.label || ''
    );
  };

  const getConvertedGenderSizeTag = (
    groupKey,
    tag,
    nextGender
  ) => {
    const information =
      getSizeTagInformation(
        groupKey,
        tag
      );

    if (
      information.mode !==
        'clothing' ||
      ![
        'メンズ',
        'レディース'
      ].includes(
        nextGender
      )
    ) {
      return tag;
    }

    const prefix =
      groupKey ===
      'labelSize'
        ? '表記サイズ'
        : '着用感';

    const convertedTag =
      `${prefix}${nextGender}${information.size}`;

    const availableValues =
      state.availableValues.get(
        groupKey
      ) || [];

    if (
      !availableValues.includes(
        convertedTag
      )
    ) {
      return '';
    }

    const storeTags =
      new Set(
        state.master
          ?.allStoreTags || []
      );

    if (
      storeTags.size > 0 &&
      !storeTags.has(
        convertedTag
      )
    ) {
      return '';
    }

    return convertedTag;
  };

  const normalizeSizeSelections = () => {
    const mode =
      getSizeMode();

    const gender =
      state.selected.get(
        'gender'
      ) || '';

    const validGender =
      [
        'メンズ',
        'レディース'
      ].includes(
        gender
      );

    const isAllGender =
      !gender;

    const labelSizeTag =
      state.selected.get(
        'labelSize'
      );

    if (labelSizeTag) {
      const information =
        getSizeTagInformation(
          'labelSize',
          labelSizeTag
        );

      if (mode === 'none') {
        state.selected.delete(
          'labelSize'
        );
      }

      else if (
        information.mode ===
        'universal'
      ) {
        // 維持
      }

      else if (mode === 'pants') {
        if (
          information.mode !==
          'pants'
        ) {
          state.selected.delete(
            'labelSize'
          );
        }
      }

      else if (
        mode === 'clothing' ||
        mode === 'mixed'
      ) {
        if (
          mode === 'mixed' &&
          information.mode ===
            'pants'
        ) {
          // 維持
        }

        else if (
          information.mode !==
            'clothing' ||
          !validGender
        ) {
          state.selected.delete(
            'labelSize'
          );
        }

        else if (
          information.gender !==
          gender
        ) {
          const convertedTag =
            getConvertedGenderSizeTag(
              'labelSize',
              labelSizeTag,
              gender
            );

          if (convertedTag) {
            state.selected.set(
              'labelSize',
              convertedTag
            );
          } else {
            state.selected.delete(
              'labelSize'
            );
          }
        }
      }
    }

    const anyFitSize =
      state.sizeAny.get(
        'fitSize'
      ) || '';

    const fitSizeTag =
      state.selected.get(
        'fitSize'
      );

    if (mode === 'none') {
      state.selected.delete(
        'fitSize'
      );

      state.sizeAny.delete(
        'fitSize'
      );

      return;
    }

    if (mode === 'pants') {
      state.sizeAny.delete(
        'fitSize'
      );

      if (fitSizeTag) {
        const information =
          getSizeTagInformation(
            'fitSize',
            fitSizeTag
          );

        if (
          information.mode !==
          'pants'
        ) {
          state.selected.delete(
            'fitSize'
          );
        }
      }

      return;
    }

    if (anyFitSize) {
      if (isAllGender) {
        state.selected.delete(
          'fitSize'
        );
        return;
      }

      if (validGender) {
        const convertedTag =
          getFitSizeTag(
            gender,
            anyFitSize
          );

        const availableValues =
          state.availableValues.get(
            'fitSize'
          ) || [];

        const storeTags =
          new Set(
            state.master
              ?.allStoreTags || []
          );

        const exists =
          availableValues.includes(
            convertedTag
          ) &&
          (
            storeTags.size === 0 ||
            storeTags.has(
              convertedTag
            )
          );

        state.sizeAny.delete(
          'fitSize'
        );

        state.selected.delete(
          'fitSize'
        );

        if (exists) {
          state.selected.set(
            'fitSize',
            convertedTag
          );
        }

        return;
      }

      state.sizeAny.delete(
        'fitSize'
      );

      state.selected.delete(
        'fitSize'
      );

      return;
    }

    if (!fitSizeTag) {
      return;
    }

    const information =
      getSizeTagInformation(
        'fitSize',
        fitSizeTag
      );

    if (
      mode === 'mixed' &&
      information.mode ===
        'pants'
    ) {
      return;
    }

    if (
      information.mode !==
      'clothing'
    ) {
      state.selected.delete(
        'fitSize'
      );
      return;
    }

    if (isAllGender) {
      state.selected.delete(
        'fitSize'
      );

      state.sizeAny.set(
        'fitSize',
        information.size
      );

      return;
    }

    if (!validGender) {
      state.selected.delete(
        'fitSize'
      );
      return;
    }

    if (
      information.gender !==
      gender
    ) {
      const convertedTag =
        getConvertedGenderSizeTag(
          'fitSize',
          fitSizeTag,
          gender
        );

      if (convertedTag) {
        state.selected.set(
          'fitSize',
          convertedTag
        );
      } else {
        state.selected.delete(
          'fitSize'
        );
      }
    }
  };

  const updateSizeGroupVisibility = () => {
    const mode =
      getSizeMode();

    const selectedGender =
      state.selected.get(
        'gender'
      ) || '';

    const validGender =
      [
        'メンズ',
        'レディース'
      ].includes(
        selectedGender
      );

    const isAllGender =
      !selectedGender;

    [
      'labelSize',
      'fitSize'
    ].forEach((groupKey) => {
      const details =
        document.querySelector(
          `[data-nonstaff-filter-group][data-group-key="${groupKey}"]`
        );

      if (!details) {
        return;
      }

      if (mode === 'none') {
        details.hidden = true;
        details.open = false;
        return;
      }

      const groupLabel =
        details.querySelector(
          '[data-nonstaff-group-label]'
        );

      if (groupLabel) {
        groupLabel.textContent =
          getSizeGroupLabel(
            groupKey
          );
      }

      let visibleCount = 0;

      details
        .querySelectorAll(
          '[data-nonstaff-filter-option]'
        )
        .forEach((option) => {
          const input =
            option.querySelector(
              '[data-nonstaff-option-input]'
            );

          if (!input) {
            return;
          }

          const tag =
            input.dataset
              .tagValue || '';

          const information =
            getSizeTagInformation(
              groupKey,
              tag
            );

          let visible = false;

          if (
            groupKey ===
              'labelSize' &&
            information.mode ===
              'universal'
          ) {
            visible = true;
          }

          else if (mode === 'mixed') {
            visible =
              information.mode ===
                'clothing' ||
              information.mode ===
                'pants';
          }

          else {
            visible =
              information.mode ===
              mode;
          }

          if (
            visible &&
            information.mode ===
              'clothing'
          ) {
            if (validGender) {
              if (
                information.gender !==
                selectedGender
              ) {
                visible = false;
              }
            }

            else if (!isAllGender) {
              visible = false;
            }

            else if (
              groupKey ===
                'fitSize' &&
              information.gender ===
                'レディース'
            ) {
              visible = false;
            }
          }

          option.hidden =
            !visible;

          const unavailableInStore =
            input.dataset
              .existsInStore ===
            'false';

          const isAllFitSize =
            visible &&
            groupKey ===
              'fitSize' &&
            information.mode ===
              'clothing' &&
            isAllGender;

          const optionLabel =
            option.querySelector(
              '[data-nonstaff-option-label]'
            );

          if (optionLabel) {
            optionLabel.textContent =
              isAllFitSize
                ? information.size
                : getDisplayLabel(
                    groupKey,
                    tag
                  );
          }

          const requiresGender =
            visible &&
            information.mode ===
              'clothing' &&
            !validGender &&
            !isAllFitSize;

          const unavailableForAllFitSize =
            isAllFitSize &&
            !fitSizeExistsForAnyGender(
              information.size
            );

          const disabled =
            visible &&
            (
              isAllFitSize
                ? unavailableForAllFitSize
                : (
                    unavailableInStore ||
                    requiresGender
                  )
            );

          input.disabled =
            disabled;

          option.classList.toggle(
            'is-disabled',
            disabled
          );

          option.setAttribute(
            'aria-disabled',
            disabled
              ? 'true'
              : 'false'
          );

          if (requiresGender) {
            option.title =
              'MENまたはWOMENを選択してください';
          }

          else if (
            unavailableForAllFitSize ||
            (
              !isAllFitSize &&
              unavailableInStore
            )
          ) {
            option.title =
              '現在、このサイズの商品はありません';
          }

          else {
            option.removeAttribute(
              'title'
            );
          }

          if (visible) {
            visibleCount += 1;
          }
        });

      details.hidden =
        visibleCount === 0;

      if (details.hidden) {
        details.open = false;
      }
    });
  };

  /* =========================================================
     Brand
  ========================================================= */

  const getNonBrandTagSet = () => {
    const tags = new Set();

    Object.entries(
      state.master
        ?.groups || {}
    ).forEach(
      ([groupKey, group]) => {
        if (groupKey === 'brand') {
          return;
        }

        (
          group.values || []
        ).forEach((tag) => {
          tags.add(tag);
        });
      }
    );

    return tags;
  };

  const getBrandRankingUrl = (
    page = 1
  ) => {
    const url =
      new URL(
        '/search',
        window.location.origin
      );

    url.searchParams.set(
      'view',
      'cross-sell'
    );

    url.searchParams.set(
      'type',
      'product'
    );

    url.searchParams.set(
      'q',
      'tag:tanpin'
    );

    url.searchParams.set(
      'page',
      String(page)
    );

    const currentParams =
      new URLSearchParams(
        window.location.search
      );

    const previewThemeId =
      currentParams.get(
        'preview_theme_id'
      );

    if (previewThemeId) {
      url.searchParams.set(
        'preview_theme_id',
        previewThemeId
      );
    }

    return url.toString();
  };

  const fetchAvailableSingleProducts =
    async () => {
      const products = [];

      for (
        let page = 1;
        page <= 5;
        page += 1
      ) {
        const response =
          await fetch(
            getBrandRankingUrl(
              page
            ),
            {
              credentials:
                'same-origin'
            }
          );

        if (!response.ok) {
          throw new Error(
            `Brand ranking request failed: ${response.status}`
          );
        }

        const data =
          await response.json();

        const pageProducts =
          Array.isArray(
            data.products
          )
            ? data.products
            : [];

        pageProducts
          .filter((product) => {
            return product.available;
          })
          .forEach((product) => {
            products.push(
              product
            );
          });

        if (
          pageProducts.length === 0
        ) {
          break;
        }

        if (
          !data.nextPage &&
          pageProducts.length < 250
        ) {
          break;
        }
      }

      return products;
    };

  const calculatePopularBrands = (
    products
  ) => {
    const availableBrands =
      new Set(
        state.availableValues
          .get('brand') || []
      );

    const counts = new Map();

    products.forEach(
      (product) => {
        const tags =
          Array.isArray(
            product.tags
          )
            ? product.tags
            : [];

        tags.forEach((tag) => {
          if (
            !availableBrands.has(tag)
          ) {
            return;
          }

          const normalizedTag =
            normalizeSearchText(tag);

          if (
            normalizedTag ===
              'unknown' ||
            normalizedTag ===
              '不明'
          ) {
            return;
          }

          counts.set(
            tag,
            (
              counts.get(tag) || 0
            ) + 1
          );
        });
      }
    );

    state.brandCounts = counts;

    state.popularBrands = [
      ...counts.entries()
    ]
      .sort((a, b) => {
        if (
          b[1] !== a[1]
        ) {
          return b[1] - a[1];
        }

        return a[0]
          .localeCompare(
            b[0],
            'ja',
            {
              numeric: true,
              sensitivity:
                'base'
            }
          );
      })
      .slice(0, 5)
      .map(
        ([brand]) => brand
      );
  };

  const restoreBrandOriginalOrder = (
    details
  ) => {
    const container =
      details.querySelector(
        '[data-nonstaff-group-options]'
      );

    if (!container) {
      return;
    }

    const options = [
      ...container
        .querySelectorAll(
          '[data-nonstaff-filter-option]'
        )
    ];

    options
      .sort((a, b) => {
        return (
          Number(
            a.dataset
              .brandOriginalIndex || 0
          ) -
          Number(
            b.dataset
              .brandOriginalIndex || 0
          )
        );
      })
      .forEach((option) => {
        container.appendChild(
          option
        );
      });
  };

  const movePopularBrandsToTop = (
    details
  ) => {
    const container =
      details.querySelector(
        '[data-nonstaff-group-options]'
      );

    if (!container) {
      return;
    }

    const optionMap =
      new Map();

    [
      ...container
        .querySelectorAll(
          '[data-nonstaff-filter-option]'
        )
    ].forEach((option) => {
      const input =
        option.querySelector(
          '[data-nonstaff-option-input]'
        );

      const tag =
        input?.dataset
          .tagValue || '';

      if (tag) {
        optionMap.set(
          tag,
          option
        );
      }
    });

    state.popularBrands
      .forEach((brand) => {
        const option =
          optionMap.get(brand);

        if (option) {
          container.appendChild(
            option
          );
        }
      });
  };

  const updateBrandGroupDisplay = (
    details
  ) => {
    if (!details) {
      return;
    }

    const searchInput =
      details.querySelector(
        '[data-nonstaff-brand-search]'
      );

    const searchValue =
      normalizeSearchText(
        searchInput?.value || ''
      );

    const title =
      details.querySelector(
        '[data-nonstaff-popular-brands-title]'
      );

    const toggleButton =
      details.querySelector(
        '[data-nonstaff-brand-toggle]'
      );

    const options = [
      ...details
        .querySelectorAll(
          '[data-nonstaff-filter-option]'
        )
    ];

    const emptyMessage =
      details.querySelector(
        '[data-nonstaff-group-empty]'
      );

    if (searchValue) {
      restoreBrandOriginalOrder(
        details
      );

      let visibleCount = 0;

      options.forEach((option) => {
        const searchText =
          option.dataset
            .searchText || '';

        const visible =
          searchText.includes(
            searchValue
          );

        option.hidden =
          !visible;

        if (visible) {
          visibleCount += 1;
        }
      });

      if (title) {
        title.hidden = true;
      }

      if (toggleButton) {
        toggleButton.hidden =
          true;
      }

      if (emptyMessage) {
        emptyMessage.hidden =
          visibleCount !== 0;
      }

      return;
    }

    const popularSet =
      new Set(
        state.popularBrands
      );

    const selectedBrand =
      state.selected.get(
        'brand'
      ) || '';

    if (
      selectedBrand &&
      !popularSet.has(
        selectedBrand
      )
    ) {
      details.dataset
        .brandExpanded =
        'true';
    }

    const expanded =
      details.dataset
        .brandExpanded ===
      'true';

    if (
      !state.brandRankingReady
    ) {
      options.forEach((option) => {
        option.hidden = false;
      });

      if (title) {
        title.hidden = true;
      }

      if (toggleButton) {
        toggleButton.hidden =
          true;
      }

      return;
    }

    if (expanded) {
      restoreBrandOriginalOrder(
        details
      );

      options.forEach((option) => {
        option.hidden = false;
      });
    } else {
      movePopularBrandsToTop(
        details
      );

      options.forEach((option) => {
        const input =
          option.querySelector(
            '[data-nonstaff-option-input]'
          );

        const tag =
          input?.dataset
            .tagValue || '';

        option.hidden =
          !popularSet.has(tag);
      });
    }

    if (title) {
      title.hidden =
        expanded;
    }

    if (toggleButton) {
      toggleButton.hidden =
        state.popularBrands
          .length === 0;

      toggleButton.textContent =
        expanded
          ? 'よく選ばれるブランドだけ見る'
          : 'すべてのブランドを見る';
    }

    if (emptyMessage) {
      emptyMessage.hidden = true;
    }
  };

  const loadPopularBrands =
    async () => {
      if (
        state.brandRankingPromise
      ) {
        return (
          state.brandRankingPromise
        );
      }

      state.brandRankingPromise =
        (async () => {
          try {
            const products =
              await fetchAvailableSingleProducts();

            calculatePopularBrands(
              products
            );

            state.brandRankingReady =
              true;

            const details =
              document.querySelector(
                '[data-nonstaff-filter-group][data-group-key="brand"]'
              );

            updateBrandGroupDisplay(
              details
            );
          } catch (error) {
            console.error(
              'Non-Staff: ブランドランキングを取得できませんでした。',
              error
            );

            state.brandRankingReady =
              false;
          }
        })();

      return (
        state.brandRankingPromise
      );
    };

  const getDynamicBrands = () => {
    const storeTags =
      unique(
        state.master
          ?.allStoreTags || []
      );

    const storeTagSet =
      new Set(storeTags);

    const nonBrandTags =
      getNonBrandTagSet();

    const knownBrands =
      new Set(
        state.master
          ?.knownBrands || []
      );

    const alwaysBrands =
      new Set(
        state.master
          ?.brandDetection
          ?.alwaysBrands || []
      );

    const separator =
      state.master
        ?.brandDetection
        ?.separator || '/';

    const detectedBrands =
      storeTags.filter((tag) => {
        if (
          nonBrandTags.has(tag)
        ) {
          return false;
        }

        return (
          knownBrands.has(tag) ||
          alwaysBrands.has(tag) ||
          tag.includes(separator)
        );
      });

    if (
      storeTags.length === 0
    ) {
      return unique(
        state.master
          ?.knownBrands || []
      );
    }

    return unique(
      detectedBrands
    )
      .filter((tag) => {
        return storeTagSet.has(
          tag
        );
      })
      .sort((a, b) => {
        return a.localeCompare(
          b,
          'ja',
          {
            numeric: true,
            sensitivity: 'base'
          }
        );
      });
  };

  const getBrandKeywordTags = (
    rawKeyword
  ) => {
    const keyword =
      normalizeSearchText(
        rawKeyword
      );

    if (!keyword) {
      return [];
    }

    const separator =
      state.master
        ?.brandDetection
        ?.separator || '/';

    const brands =
      state.availableValues
        .get('brand') ||
      getDynamicBrands();

    return brands.filter(
      (brandTag) => {
        const aliases =
          unique([
            brandTag,
            ...String(
              brandTag || ''
            )
              .split(separator)
              .map(
                (part) =>
                  part.trim()
              )
          ])
            .map(
              normalizeSearchText
            );

        return aliases.includes(
          keyword
        );
      }
    );
  };

  const getItemKeywordTags = (
    rawKeyword
  ) => {
    const keyword =
      normalizeSearchText(
        rawKeyword
      );

    if (!keyword) {
      return [];
    }

    const itemValues =
      state.availableValues
        .get('item') ||
      state.master
        ?.groups
        ?.item
        ?.values ||
      [];

    const matchedItem =
      itemValues.find((tag) => {
        return (
          normalizeSearchText(tag) ===
            keyword ||
          normalizeSearchText(
            getDisplayLabel(
              'item',
              tag
            )
          ) === keyword
        );
      });

    if (!matchedItem) {
      return [];
    }

    return getSearchTagsForSelection(
      'item',
      matchedItem
    );
  };

  const buildStructuredKeywordClause = (
    rawKeyword
  ) => {
    const brandTags =
      getBrandKeywordTags(
        rawKeyword
      );

    if (
      brandTags.length > 0
    ) {
      return buildTagClause(
        brandTags
      );
    }

    const itemTags =
      getItemKeywordTags(
        rawKeyword
      );

    if (
      itemTags.length > 0
    ) {
      return buildTagClause(
        itemTags
      );
    }

    return '';
  };

  const buildFreeKeywordClause = (
    rawKeyword
  ) => {
    const terms =
      String(rawKeyword || '')
        .normalize('NFKC')
        .trim()
        .split(/\s+/)
        .filter(Boolean);

    return terms
      .map((term) => {
        return `"${escapeSearchValue(term)}"`;
      })
      .join(' AND ');
  };

  /* =========================================================
     Available values
  ========================================================= */

  const getAvailableValues = (
    groupKey
  ) => {
    const group =
      state.master
        ?.groups?.[groupKey];

    if (!group) {
      return [];
    }

    if (
      groupKey === 'brand' ||
      group.dynamic
    ) {
      return getDynamicBrands();
    }

    const masterValues =
      unique(
        group.values || []
      );

    const storeTags =
      unique(
        state.master
          ?.allStoreTags || []
      );

    if (
      [
        'labelSize',
        'fitSize'
      ].includes(groupKey)
    ) {
      return masterValues;
    }

    if (
      storeTags.length === 0
    ) {
      return masterValues;
    }

    const storeTagSet =
      new Set(storeTags);

    if (
      groupKey === 'item'
    ) {
      return masterValues
        .filter((tag) => {
          return (
            getSearchTagsForSelection(
              groupKey,
              tag
            )
              .some(
                (searchTag) =>
                  storeTagSet.has(
                    searchTag
                  )
              )
          );
        });
    }

    return masterValues
      .filter((tag) => {
        return storeTagSet.has(
          tag
        );
      });
  };

  const prepareAvailableValues = () => {
    state.availableValues.clear();

    state.master
      .groupOrder
      .forEach((groupKey) => {
        state.availableValues.set(
          groupKey,
          getAvailableValues(
            groupKey
          )
        );
      });
  };

  const findGroupForTag = (
    tag
  ) => {
    for (
      const groupKey of
      state.master.groupOrder
    ) {
      const values =
        state.availableValues
          .get(groupKey) || [];

      if (values.includes(tag)) {
        return groupKey;
      }
    }

    return null;
  };

  /* =========================================================
     URL → State
  ========================================================= */

  const hydrateSelectionsFromUrl = () => {
    const params =
      new URLSearchParams(
        window.location.search
      );

    state.master
      .groupOrder
      .forEach((groupKey) => {
        const tag =
          params.get(
            `ns_${groupKey}`
          );

        const availableValues =
          state.availableValues
            .get(groupKey) || [];

        if (
          tag &&
          availableValues
            .includes(tag)
        ) {
          state.selected.set(
            groupKey,
            tag
          );
        }
      });

    params
      .getAll(
        'filter.p.tag'
      )
      .forEach((tag) => {
        const groupKey =
          findGroupForTag(tag);

        if (
          groupKey &&
          !state.selected.has(
            groupKey
          )
        ) {
          state.selected.set(
            groupKey,
            tag
          );
        }
      });

    const displayKeyword =
      params.get(
        'display_q'
      ) || '';

    const internalKeyword =
      params.get(
        'q'
      ) || '';

    const virtualSize =
      params.get(
        'ns_fitSizeAny'
      ) || '';

    state.sizeAny.delete(
      'fitSize'
    );

    if (virtualSize) {
      state.selected.delete(
        'fitSize'
      );

      state.sizeAny.set(
        'fitSize',
        virtualSize
      );
    }

    let readableKeyword = '';

    if (displayKeyword) {
      readableKeyword =
        displayKeyword;
    } else {
      const hasInternalTagQuery =
        /(^|\s|\()tag:/.test(
          internalKeyword
        );

      const titleTerms = [
        ...internalKeyword
          .matchAll(
            /title:"((?:\\.|[^"])*)"/g
          )
      ].map((match) => {
        return match[1]
          .replace(
            /\\"/g,
            '"'
          )
          .replace(
            /\\\\/g,
            '\\'
          );
      });

      if (
        titleTerms.length > 0
      ) {
        readableKeyword =
          titleTerms.join(' ');
      }

      else if (
        !hasInternalTagQuery &&
        internalKeyword &&
        !/\s+(AND|OR)\s+/i.test(
          internalKeyword
        )
      ) {
        readableKeyword =
          internalKeyword
            .replace(
              /^"|"$/g,
              ''
            );
      }
    }

    if (
      state.keywordInput
    ) {
      state.keywordInput.value =
        readableKeyword;
    }
  };

  /* =========================================================
     Gender Tabs
  ========================================================= */

  const createGenderButton = (
    value,
    label,
    disabled = false
  ) => {
    const button =
      document.createElement(
        'button'
      );

    button.type = 'button';

    button.className =
      'nonstaff-gender-tab';

    button.dataset
      .nonstaffGenderValue =
      value;

    button.textContent =
      label;

    button.disabled =
      disabled;

    button.setAttribute(
      'aria-pressed',
      'false'
    );

    button.setAttribute(
      'aria-disabled',
      disabled
        ? 'true'
        : 'false'
    );

    button.addEventListener(
      'click',
      () => {
        if (disabled) {
          return;
        }

        const currentAnySize =
          state.sizeAny.get(
            'fitSize'
          ) || '';

        const currentFitSizeTag =
          state.selected.get(
            'fitSize'
          ) || '';

        const currentFitSizeInformation =
          currentFitSizeTag
            ? getSizeTagInformation(
                'fitSize',
                currentFitSizeTag
              )
            : {
                mode: 'other',
                gender: '',
                size: ''
              };

        const logicalFitSize =
          currentAnySize ||
          (
            currentFitSizeInformation
              .mode === 'clothing'
              ? currentFitSizeInformation
                  .size
              : ''
          );

        if (value === '') {
          state.selected.delete(
            'gender'
          );

          if (logicalFitSize) {
            state.selected.delete(
              'fitSize'
            );

            state.sizeAny.set(
              'fitSize',
              logicalFitSize
            );
          }

          updateInterface();
          return;
        }

        state.selected.set(
          'gender',
          value
        );

        if (
          [
            'メンズ',
            'レディース'
          ].includes(value) &&
          logicalFitSize
        ) {
          const convertedTag =
            getFitSizeTag(
              value,
              logicalFitSize
            );

          const availableValues =
            state.availableValues
              .get('fitSize') || [];

          const storeTags =
            new Set(
              state.master
                ?.allStoreTags || []
            );

          const exists =
            availableValues.includes(
              convertedTag
            ) &&
            (
              storeTags.size === 0 ||
              storeTags.has(
                convertedTag
              )
            );

          state.sizeAny.delete(
            'fitSize'
          );

          state.selected.delete(
            'fitSize'
          );

          if (exists) {
            state.selected.set(
              'fitSize',
              convertedTag
            );
          }

          updateInterface();
          return;
        }

        if (
          value ===
            'ユニセックス' &&
          logicalFitSize
        ) {
          state.sizeAny.delete(
            'fitSize'
          );

          state.selected.delete(
            'fitSize'
          );
        }

        updateInterface();
      }
    );

    return button;
  };

  const renderGenderTabs = () => {
    const container =
      document.querySelector(
        SELECTORS.genderTabs
      );

    if (!container) {
      return;
    }

    container.replaceChildren();

    const availableGenderValues =
      new Set(
        state.availableValues
          .get('gender') || []
      );

    const genderValues = [
      'メンズ',
      'レディース',
      'ユニセックス'
    ];

    container.appendChild(
      createGenderButton(
        '',
        'ALL'
      )
    );

    genderValues.forEach(
      (value) => {
        container.appendChild(
          createGenderButton(
            value,
            getGenderTabLabel(
              value
            ),
            !availableGenderValues
              .has(value)
          )
        );
      }
    );
  };

  /* =========================================================
     Options
  ========================================================= */

  const createOption = (
    groupKey,
    tag
  ) => {
    const template =
      document.querySelector(
        SELECTORS.optionTemplate
      );

    if (!template) {
      return null;
    }

    const fragment =
      template.content
        .cloneNode(true);

    const label =
      fragment.querySelector(
        '[data-nonstaff-filter-option]'
      );

    const input =
      fragment.querySelector(
        '[data-nonstaff-option-input]'
      );

    const labelText =
      fragment.querySelector(
        '[data-nonstaff-option-label]'
      );

    if (
      !label ||
      !input ||
      !labelText
    ) {
      return null;
    }

    const safeId =
      `Nonstaff-${groupKey}-${Math.random()
        .toString(36)
        .slice(2, 10)}`;

    input.id = safeId;

    input.name =
      `nonstaff-${groupKey}`;

    input.value = tag;

    input.dataset.groupKey =
      groupKey;

    input.dataset.tagValue =
      tag;

    const isSizeGroup =
      [
        'labelSize',
        'fitSize'
      ].includes(
        groupKey
      );

    const storeTags =
      new Set(
        state.master
          ?.allStoreTags || []
      );

    const existsInStore =
      storeTags.size === 0 ||
      (
        groupKey === 'item'
          ? getSearchTagsForSelection(
              groupKey,
              tag
            ).some(
              (searchTag) =>
                storeTags.has(
                  searchTag
                )
            )
          : storeTags.has(tag)
      );

    input.dataset
      .existsInStore =
      existsInStore
        ? 'true'
        : 'false';

    if (
      isSizeGroup &&
      !existsInStore
    ) {
      input.disabled = true;

      label.classList.add(
        'is-disabled'
      );

      label.setAttribute(
        'aria-disabled',
        'true'
      );

      label.title =
        '現在、このサイズの商品はありません';
    }

    label.setAttribute(
      'for',
      safeId
    );

    label.dataset.searchText =
      normalizeSearchText(tag);

    labelText.textContent =
      getDisplayLabel(
        groupKey,
        tag
      );

    input.addEventListener(
      'change',
      () => {
        if (!input.checked) {
          return;
        }

        const information =
          getSizeTagInformation(
            groupKey,
            tag
          );

        const selectedGender =
          state.selected.get(
            'gender'
          ) || '';

        const isAllFitSize =
          groupKey ===
            'fitSize' &&
          information.mode ===
            'clothing' &&
          !selectedGender;

        if (isAllFitSize) {
          state.selected.delete(
            groupKey
          );

          state.sizeAny.set(
            groupKey,
            information.size
          );
        } else {
          state.sizeAny.delete(
            groupKey
          );

          state.selected.set(
            groupKey,
            tag
          );
        }

        updateInterface();
      }
    );

    return fragment;
  };

  const filterBrandOptions = (
    details,
    searchValue
  ) => {
    const input =
      details.querySelector(
        '[data-nonstaff-brand-search]'
      );

    if (
      input &&
      input.value !==
        searchValue
    ) {
      input.value =
        searchValue;
    }

    updateBrandGroupDisplay(
      details
    );
  };

  const createFilterGroup = (
    groupKey
  ) => {
    const group =
      state.master
        ?.groups?.[groupKey];

    const values =
      state.availableValues
        .get(groupKey) || [];

    if (
      !group ||
      values.length === 0
    ) {
      return null;
    }

    const template =
      document.querySelector(
        SELECTORS.groupTemplate
      );

    if (!template) {
      return null;
    }

    const fragment =
      template.content
        .cloneNode(true);

    const details =
      fragment.querySelector(
        '[data-nonstaff-filter-group]'
      );

    const label =
      fragment.querySelector(
        '[data-nonstaff-group-label]'
      );

    const optionsContainer =
      fragment.querySelector(
        '[data-nonstaff-group-options]'
      );

    const brandSearchArea =
      fragment.querySelector(
        '[data-nonstaff-brand-search-area]'
      );

    const brandSearchInput =
      fragment.querySelector(
        '[data-nonstaff-brand-search]'
      );

    if (
      !details ||
      !label ||
      !optionsContainer
    ) {
      return null;
    }

    details.dataset.groupKey =
      groupKey;

    label.textContent =
      group.label;

    values.forEach((tag) => {
      const option =
        createOption(
          groupKey,
          tag
        );

      if (option) {
        optionsContainer
          .appendChild(
            option
          );
      }
    });

    if (
      groupKey === 'brand' &&
      brandSearchArea &&
      brandSearchInput
    ) {
      brandSearchArea.hidden =
        false;

      [
        ...optionsContainer
          .querySelectorAll(
            '[data-nonstaff-filter-option]'
          )
      ].forEach(
        (option, index) => {
          option.dataset
            .brandOriginalIndex =
            String(index);
        }
      );

      const popularTitle =
        document.createElement(
          'p'
        );

      popularTitle.className =
        'nonstaff-brand-popular-title';

      popularTitle.dataset
        .nonstaffPopularBrandsTitle =
        '';

      popularTitle.textContent =
        'よく選ばれるブランド';

      popularTitle.hidden =
        true;

      optionsContainer
        .insertAdjacentElement(
          'beforebegin',
          popularTitle
        );

      const toggleButton =
        document.createElement(
          'button'
        );

      toggleButton.type =
        'button';

      toggleButton.className =
        'nonstaff-brand-toggle';

      toggleButton.dataset
        .nonstaffBrandToggle =
        '';

      toggleButton.textContent =
        'すべてのブランドを見る';

      toggleButton.hidden =
        true;

      optionsContainer
        .insertAdjacentElement(
          'afterend',
          toggleButton
        );

      toggleButton
        .addEventListener(
          'click',
          () => {
            const expanded =
              details.dataset
                .brandExpanded ===
              'true';

            details.dataset
              .brandExpanded =
              expanded
                ? 'false'
                : 'true';

            updateBrandGroupDisplay(
              details
            );
          }
        );

      brandSearchInput
        .addEventListener(
          'input',
          () => {
            updateBrandGroupDisplay(
              details
            );
          }
        );
    }

    return fragment;
  };

  const renderFilterGroups = () => {
    const container =
      document.querySelector(
        SELECTORS.filterGroups
      );

    if (!container) {
      return;
    }

    container.replaceChildren();

    state.master
      .groupOrder
      .forEach((groupKey) => {
        if (
          groupKey === 'gender' ||
          groupKey === 'item'
        ) {
          return;
        }

        const group =
          createFilterGroup(
            groupKey
          );

        if (group) {
          container.appendChild(
            group
          );
        }
      });
  };

  /* =========================================================
     Interface
  ========================================================= */

  const updateGenderTabs = () => {
    const selectedGender =
      state.selected.get(
        'gender'
      ) || '';

    document
      .querySelectorAll(
        '[data-nonstaff-gender-value]'
      )
      .forEach((button) => {
        const selected =
          button.dataset
            .nonstaffGenderValue ===
          selectedGender;

        button.classList.toggle(
          'is-selected',
          selected
        );

        button.setAttribute(
          'aria-pressed',
          selected
            ? 'true'
            : 'false'
        );
      });
  };

  const updateOptionInputs = () => {
    state.drawer
      .querySelectorAll(
        '[data-nonstaff-option-input]'
      )
      .forEach((input) => {
        const groupKey =
          input.dataset
            .groupKey || '';

        const tag =
          input.dataset
            .tagValue || '';

        const information =
          getSizeTagInformation(
            groupKey,
            tag
          );

        const selectedGender =
          state.selected.get(
            'gender'
          ) || '';

        const anySize =
          state.sizeAny.get(
            groupKey
          ) || '';

        const option =
          input.closest(
            '[data-nonstaff-filter-option]'
          );

        const isAllFitSizeSelected =
          groupKey ===
            'fitSize' &&
          information.mode ===
            'clothing' &&
          !selectedGender &&
          anySize ===
            information.size &&
          !option?.hidden;

        input.checked =
          isAllFitSizeSelected ||
          (
            !anySize &&
            state.selected.get(
              groupKey
            ) === tag
          );
      });
  };

  const updateGroupCurrentLabels = () => {
    document
      .querySelectorAll(
        '[data-nonstaff-filter-group]'
      )
      .forEach((details) => {
        const groupKey =
          details.dataset
            .groupKey;

        const current =
          details.querySelector(
            '[data-nonstaff-group-current]'
          );

        const selectedTag =
          state.selected.get(
            groupKey
          );

        const anySize =
          state.sizeAny.get(
            groupKey
          ) || '';

        if (!current) {
          return;
        }

        current.textContent =
          anySize ||
          (
            selectedTag
              ? getDisplayLabel(
                  groupKey,
                  selectedTag
                )
              : ''
          );

        details.classList.toggle(
          'has-selection',
          Boolean(
            selectedTag ||
            anySize
          )
        );
      });
  };

  const removeSelection = (
    groupKey
  ) => {
    state.selected.delete(
      groupKey
    );

    state.sizeAny.delete(
      groupKey
    );

    updateInterface();
  };

  const renderSelectedTags = () => {
    const area =
      document.querySelector(
        SELECTORS.selectedArea
      );

    const list =
      document.querySelector(
        SELECTORS.selectedList
      );

    const template =
      document.querySelector(
        SELECTORS.selectedTemplate
      );

    if (
      !area ||
      !list ||
      !template
    ) {
      return;
    }

    list.replaceChildren();

    let selectedCount = 0;

    state.master
      .groupOrder
      .forEach((groupKey) => {
        const tag =
          state.selected.get(
            groupKey
          );

        const anySize =
          state.sizeAny.get(
            groupKey
          ) || '';

        if (
          !tag &&
          !anySize
        ) {
          return;
        }

        const fragment =
          template.content
            .cloneNode(true);

        const button =
          fragment.querySelector(
            '[data-nonstaff-selected-tag]'
          );

        const label =
          fragment.querySelector(
            '[data-nonstaff-selected-tag-label]'
          );

        if (
          !button ||
          !label
        ) {
          return;
        }

        label.textContent =
          anySize ||
          getDisplayLabel(
            groupKey,
            tag
          );

        button.dataset.groupKey =
          groupKey;

        button.addEventListener(
          'click',
          () => {
            removeSelection(
              groupKey
            );
          }
        );

        list.appendChild(
          fragment
        );

        selectedCount += 1;
      });

    area.hidden =
      selectedCount === 0;
  };

  const updateKeywordClearButton = () => {
    const clearButton =
      document.querySelector(
        SELECTORS.keywordClear
      );

    if (
      !clearButton ||
      !state.keywordInput
    ) {
      return;
    }

    clearButton.hidden =
      state.keywordInput
        .value
        .trim() === '';
  };

  const updateInterface = () => {
    normalizeItemSelection();
    normalizeSizeSelections();

    updateItemGroupVisibility();
    updateSizeGroupVisibility();

    updateGenderTabs();
    updateOptionInputs();
    updateGroupCurrentLabels();
    renderSelectedTags();
    updateKeywordClearButton();
  };

  const clearAllConditions = () => {
    state.selected.clear();
    state.sizeAny.clear();

    if (
      state.keywordInput
    ) {
      state.keywordInput.value =
        '';
    }

    document
      .querySelectorAll(
        '[data-nonstaff-brand-search]'
      )
      .forEach((input) => {
        input.value = '';

        const details =
          input.closest(
            '[data-nonstaff-filter-group]'
          );

        if (details) {
          filterBrandOptions(
            details,
            ''
          );
        }
      });

    document
      .querySelectorAll(
        '[data-nonstaff-filter-group]'
      )
      .forEach((details) => {
        details.open = false;
      });

    updateInterface();

    state.keywordInput
      ?.focus();
  };

  /* =========================================================
     Build Search URL
  ========================================================= */

  const buildVirtualSizeClause = (
    size
  ) => {
    if (!size) {
      return '';
    }

    const menTag =
      getFitSizeTag(
        'メンズ',
        size
      );

    const womenTag =
      getFitSizeTag(
        'レディース',
        size
      );

    return buildTagClause([
      menTag,
      womenTag
    ]);
  };

  const buildSearchUrl = () => {
    const keyword =
      state.keywordInput
        ?.value
        .trim() || '';

    const virtualFitSize =
      state.sizeAny.get(
        'fitSize'
      ) || '';

    /*
      登録ブランドと完全一致したキーワードは、
      Shopifyの通常検索ではなく商品タグで完全一致させる。

      例:
      ディッキーズ
      → filter.p.tag=Dickies/ディッキーズ
    */
    const exactBrandTags =
      keyword
        ? getBrandKeywordTags(
            keyword
          )
        : [];

    const hasExactBrandKeyword =
      exactBrandTags.length > 0;

    const selectedEntries =
      state.master
        .groupOrder
        .map((groupKey) => {
          return [
            groupKey,
            state.selected.get(
              groupKey
            ) || ''
          ];
        })
        .filter(([, tag]) => {
          return Boolean(tag);
        });

    const root =
      window.Shopify
        ?.routes?.root || '/';

    const useSearchRoute =
      Boolean(
        keyword ||
        virtualFitSize ||
        selectedEntries.length > 0
      );

    const baseUrl =
      useSearchRoute
        ? state.form.action
        : `${root}collections/search-results`;

    const url =
      new URL(
        baseUrl,
        window.location.origin
      );

    const currentParams =
      new URLSearchParams(
        window.location.search
      );

    const previewThemeId =
      currentParams.get(
        'preview_theme_id'
      );

    if (previewThemeId) {
      url.searchParams.set(
        'preview_theme_id',
        previewThemeId
      );
    }

    const availabilityValue =
      currentParams.get(
        'filter.v.availability'
      );

    const availabilityMode =
      currentParams.get(
        'ns_availability'
      );

    if (
      availabilityValue === '1'
    ) {
      url.searchParams.set(
        'filter.v.availability',
        '1'
      );
    }

    else if (
      availabilityMode === 'all'
    ) {
      url.searchParams.set(
        'ns_availability',
        'all'
      );
    }

    if (!useSearchRoute) {
      return url;
    }

    const clauses = [];

    /*
      検索は単品商品だけ。
    */
    clauses.push(
      buildTagClause([
        'tanpin'
      ])
    );

    if (keyword) {
      /*
        ブランド完全一致の場合は、
        「ディッキーズ」という文字そのものを
        Shopifyの通常検索には渡さない。

        これで Shopify 側の曖昧検索による
        ディズニー混入を防ぐ。
      */
      if (!hasExactBrandKeyword) {
        const structuredClause =
          buildStructuredKeywordClause(
            keyword
          );

        const keywordClause =
          structuredClause ||
          buildFreeKeywordClause(
            keyword
          );

        if (keywordClause) {
          clauses.push(
            keywordClause
          );
        }
      }

      url.searchParams.set(
        'display_q',
        keyword
      );
    }

    if (virtualFitSize) {
      const sizeClause =
        buildVirtualSizeClause(
          virtualFitSize
        );

      if (sizeClause) {
        clauses.push(
          sizeClause
        );
      }

      url.searchParams.set(
        'ns_fitSizeAny',
        virtualFitSize
      );
    }

    selectedEntries.forEach(
      ([groupKey, tag]) => {
        const tags =
          getSearchTagsForSelection(
            groupKey,
            tag
          );

        const clause =
          buildTagClause(tags);

        if (clause) {
          clauses.push(clause);
        }

        url.searchParams.set(
          `ns_${groupKey}`,
          tag
        );
      }
    );

    const searchQuery =
      unique(clauses)
        .join(' AND ');

    if (searchQuery) {
      url.searchParams.set(
        'q',
        searchQuery
      );
    }

    url.searchParams.set(
      'type',
      'product'
    );

    url.searchParams.set(
      'options[prefix]',
      'none'
    );

    /*
      ここが今回の重要部分。

      ブランド名と完全一致したら、
      Shopify標準の商品タグフィルターを付与。

      ディッキーズ
      ↓
      filter.p.tag=Dickies/ディッキーズ
    */
    if (hasExactBrandKeyword) {
      exactBrandTags.forEach(
        (brandTag) => {
          url.searchParams.append(
            'filter.p.tag',
            brandTag
          );
        }
      );
    }

    return url;
  };

  const submitSearch = (
    event
  ) => {
    event.preventDefault();

    const message =
      document.querySelector(
        SELECTORS.message
      );

    try {
      const url =
        buildSearchUrl();

      if (message) {
        message.textContent =
          '検索結果を表示しています。';
      }

      window.location.assign(
        url.toString()
      );
    } catch (error) {
      console.error(
        'Non-Staff: 検索URLを作成できませんでした。',
        error
      );

      if (message) {
        message.textContent =
          '検索を実行できませんでした。もう一度お試しください。';
      }
    }
  };

  /* =========================================================
     Drawer
  ========================================================= */

  const getFocusableElements = () => {
    if (!state.panel) {
      return [];
    }

    return [
      ...state.panel
        .querySelectorAll(
          [
            'a[href]',
            'button:not([disabled])',
            'input:not([disabled])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            '[tabindex]:not([tabindex="-1"])'
          ].join(',')
        )
    ].filter((element) => {
      return (
        !element.hidden &&
        element.offsetParent !==
          null
      );
    });
  };

  const trapFocus = (
    event
  ) => {
    if (
      event.key !== 'Tab'
    ) {
      return;
    }

    const focusableElements =
      getFocusableElements();

    if (
      focusableElements.length ===
      0
    ) {
      event.preventDefault();
      return;
    }

    const firstElement =
      focusableElements[0];

    const lastElement =
      focusableElements[
        focusableElements.length - 1
      ];

    if (
      event.shiftKey &&
      document.activeElement ===
        firstElement
    ) {
      event.preventDefault();

      lastElement.focus();
      return;
    }

    if (
      !event.shiftKey &&
      document.activeElement ===
        lastElement
    ) {
      event.preventDefault();

      firstElement.focus();
    }
  };

  const handleDrawerKeydown = (
    event
  ) => {
    if (
      event.key === 'Escape'
    ) {
      closeDrawer();
      return;
    }

    trapFocus(event);
  };

  const ensureDrawerContent = () => {
    if (state.contentReady) {
      return;
    }

    prepareAvailableValues();
    hydrateSelectionsFromUrl();
    renderGenderTabs();
    renderFilterGroups();
    updateInterface();

    state.contentReady = true;

    loadPopularBrands();
  };

  const openDrawer = (
    triggerElement = null
  ) => {
    if (!state.drawer) {
      return;
    }

    ensureDrawerContent();

    clearTimeout(
      state.closeTimer
    );

    state.lastFocusedElement =
      triggerElement instanceof
      HTMLElement
        ? triggerElement
        : document.activeElement;

    state.drawer.hidden = false;

    state.drawer.setAttribute(
      'aria-hidden',
      'false'
    );

    document
      .documentElement
      .classList.add(
        'nonstaff-search-is-open'
      );

    document.body
      .classList.add(
        'nonstaff-search-is-open'
      );

    requestAnimationFrame(
      () => {
        state.drawer
          .classList.add(
            'is-open'
          );

        state.keywordInput
          ?.focus();
      }
    );

    document.addEventListener(
      'keydown',
      handleDrawerKeydown
    );
  };

  const closeDrawer = () => {
    if (!state.drawer) {
      return;
    }

    state.drawer
      .classList.remove(
        'is-open'
      );

    state.drawer.setAttribute(
      'aria-hidden',
      'true'
    );

    document
      .documentElement
      .classList.remove(
        'nonstaff-search-is-open'
      );

    document.body
      .classList.remove(
        'nonstaff-search-is-open'
      );

    document.removeEventListener(
      'keydown',
      handleDrawerKeydown
    );

    state.closeTimer =
      window.setTimeout(
        () => {
          state.drawer.hidden =
            true;
        },
        300
      );

    if (
      state.lastFocusedElement instanceof
      HTMLElement
    ) {
      state.lastFocusedElement
        .focus();
    }
  };

  /* =========================================================
     Events
  ========================================================= */

  const bindEvents = () => {
    document
      .querySelectorAll(
        SELECTORS.open
      )
      .forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            openDrawer(
              button
            );
          }
        );
      });

    document
      .querySelectorAll(
        SELECTORS.close
      )
      .forEach((button) => {
        button.addEventListener(
          'click',
          closeDrawer
        );
      });

    document
      .querySelector(
        SELECTORS.clear
      )
      ?.addEventListener(
        'click',
        clearAllConditions
      );

    state.form
      ?.addEventListener(
        'submit',
        submitSearch
      );

    state.keywordInput
      ?.addEventListener(
        'input',
        updateKeywordClearButton
      );

    document
      .querySelector(
        SELECTORS.keywordClear
      )
      ?.addEventListener(
        'click',
        () => {
          if (
            !state.keywordInput
          ) {
            return;
          }

          state.keywordInput.value =
            '';

          updateKeywordClearButton();

          state.keywordInput
            .focus();
        }
      );

    document.addEventListener(
      'nonstaff:search:open',
      () => openDrawer()
    );

    document.addEventListener(
      'nonstaff:search:close',
      closeDrawer
    );
  };

  const exposePublicMethods = () => {
    window.NonstaffSearchDrawer = {
      open: openDrawer,
      close: closeDrawer,
      clear:
        clearAllConditions
    };
  };

  /* =========================================================
     Init
  ========================================================= */

  const init = () => {
    state.master =
      parseTagMaster();

    state.drawer =
      document.querySelector(
        SELECTORS.drawer
      );

    state.panel =
      document.querySelector(
        SELECTORS.panel
      );

    state.form =
      document.querySelector(
        SELECTORS.form
      );

    state.keywordInput =
      document.querySelector(
        SELECTORS.keyword
      );

    if (
      !state.master ||
      !state.drawer ||
      !state.form
    ) {
      return;
    }

    bindEvents();
    exposePublicMethods();
  };

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      init,
      {
        once: true
      }
    );
  } else {
    init();
  }
})();